import {
	RefObject,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

type UseResizeWidthPanelOptionType = {
	/** Размер свернутой панели */
	disabledSize?: number;
	/** Минимальный размер панели */
	min?: number;
	/** Максимальный размер панели */
	max?: number;
	/** Размер панели по умолчанию */
	defaultWidth?: number;
	/** Сохранять значение */
	enableSaving?: boolean;
	/** Наименование ключа */
	savingKey?: string;

	/**  */
	disableResize?: boolean;
};

type useResizeWidthPanelType = (
	enable: boolean,
	options?: UseResizeWidthPanelOptionType,
) => [RefObject<any>, (clientX: number) => void, boolean];

const defaultOptions = {
	disabledSize: 20,
	min: 300,
	max: 530,
	defaultWidth: 450,
	enableSaving: true,
	disableResize: false,
	savingKey: 'subMenuWidth',
};

/**
 * Хук инкапсулирует логику работы панели с изменяемой мышью размером
 * @param enable Флаг указывает включен ли функционал
 * @param disabledSize Размер панели при отключенном функционале ( в px )
 * @param minWidth Минимальный размер панели ( в px )
 * @param maxWidth Максимальный размер панели ( в px )
 * @returns Массив с ref'ом панели, функция старта ресайза, флаг указывающий на активность ресайза
 */
const useResizeWidthPanel: useResizeWidthPanelType = (
	enable = false,
	options,
) => {
	const {
		disabledSize = 20,
		min = 300,
		max = 530,
		defaultWidth = 450,
		enableSaving = false,
		savingKey = 'subMenuWidth',
		disableResize = false,
	} = options ? options : defaultOptions;

	const panelRef = useRef<any>(null);
	const [width, setWidth] = useState<number>(defaultWidth);
	const [separatorXPosition, setSeparatorXPosition] = useState<number>();
	const [dragging, setDragging] = useState<boolean>(false);

	useLayoutEffect(() => {
		if (!disableResize) {
			document.addEventListener('mousemove', onMouseMove);
			document.addEventListener('touchmove', onTouchMove);
			document.addEventListener('mouseup', onMouseUp);
			return () => {
				document.removeEventListener('mousemove', onMouseMove);
				document.removeEventListener('touchmove', onTouchMove);
				document.removeEventListener('mouseup', onMouseUp);
			};
		}
	}, [disableResize, dragging]);

	// Подтягиваем сохранённую ширину один раз при монтировании
	useEffect(() => {
		if (!enableSaving) {
			return;
		}
		const w = Number(localStorage.getItem(savingKey) ?? 0);
		if (!!w && w !== width) {
			setWidth(w);
		}
		// eslint-disable-next-line
	}, []);

	useLayoutEffect(() => {
		const node = panelRef.current;
		if (!node) {
			return;
		}
		const targetWidth = enable ? width : disabledSize;
		node.style.width = `${targetWidth}px`;
	}, [enable, width, disabledSize]);

	const onMove = (clientX: number) => {
		if (dragging && width && separatorXPosition) {
			const newLeftWidth = width + clientX - separatorXPosition;
			setSeparatorXPosition(clientX);

			if (newLeftWidth <= min) {
				setWidth(min);
				return;
			} else if (newLeftWidth >= max) {
				setWidth(max);
				return;
			}
			setWidth(newLeftWidth);
		}
	};

	const onStartResize = (clientX: number) => {
		setSeparatorXPosition(clientX);
		setDragging(true);
	};

	const onMouseMove = (e: MouseEvent) => {
		return onMove(e.clientX);
	};
	const onTouchMove = (e: TouchEvent) => {
		return onMove(e.touches[0].clientX);
	};
	const onMouseUp = () => {
		setDragging(false);
		if (enableSaving && !!width) {
			localStorage.setItem(savingKey, width.toString());
		}
	};

	return [panelRef, onStartResize, dragging];
};

export default useResizeWidthPanel;
